// NightAuditReport.js — End-of-day operational dashboard.
// Single fetch from /api/reports/night-audit fills the entire view:
// summary KPI cards + breakdown tables + audit red flags. Export options
// are browser-print (PDF-via-print-dialog) and xlsx for spreadsheet.
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Container, Card, CardContent, Typography, TextField, Button,
  Grid, Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  Chip, Snackbar, Alert, CircularProgress, Divider,
  FormControlLabel, Switch,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import * as XLSX from 'xlsx';
import config from '../../config';

const company = JSON.parse(sessionStorage.getItem('companyDetails') || '{}');
const currency = company?.currency ?? '₹';

const todayStr = () => new Date().toISOString().slice(0, 10);

const fmtMoney = (n) =>
  Number(n || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });

const KpiCard = ({ title, value, suffix, color = '#1976d2', sub }) => (
  <Card sx={{ height: '100%', borderLeft: `4px solid ${color}` }}>
    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
      <Typography variant='caption' color='text.secondary' sx={{ textTransform: 'uppercase', fontWeight: 600 }}>
        {title}
      </Typography>
      <Typography variant='h5' fontWeight='700' sx={{ mt: 0.5, color }}>
        {value}{suffix ? <span style={{ fontSize: '0.6em', marginLeft: 4 }}>{suffix}</span> : null}
      </Typography>
      {sub && (
        <Typography variant='caption' color='text.secondary'>{sub}</Typography>
      )}
    </CardContent>
  </Card>
);

const SectionTitle = ({ children }) => (
  <Typography variant='h6' fontWeight='700' color='primary' gutterBottom sx={{ mt: 3, mb: 1 }}>
    {children}
  </Typography>
);

export default function NightAuditReport() {
  const [date, setDate] = useState(todayStr());
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'success' });
  const notify = (msg, sev = 'success') => setSnack({ open: true, msg, sev });

  // Per-hotel opt-in: the backend night-audit job only emails this report when
  // this flag is ON. Default OFF — no mail unless someone explicitly allows it.
  const [emailAllowed, setEmailAllowed] = useState(false);
  const [emailSaving, setEmailSaving] = useState(false);

  const hotelId = company?.hotelId || company?.id;
  const companyId = company?.id;

  const load = async () => {
    if (!companyId || !hotelId) return;
    setLoading(true);
    try {
      const token = sessionStorage.getItem('token');
      const res = await fetch(
        `${config.API_BASE_URL}/reports/night-audit?companyId=${companyId}&hotelId=${hotelId}&date=${date}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) throw new Error(await res.text());
      const json = await res.json();
      setData(json);
    } catch (e) {
      notify('Failed to load night audit', 'error');
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const loadEmailSetting = async () => {
    if (!companyId || !hotelId) return;
    try {
      const token = sessionStorage.getItem('token');
      const res = await fetch(
        `${config.API_BASE_URL}/reports/night-audit/email-setting?companyId=${companyId}&hotelId=${hotelId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) return;
      const json = await res.json();
      setEmailAllowed(Boolean(json?.enabled));
    } catch (e) {
      console.error(e);
    }
  };

  // Persist the opt-in. Optimistic update, revert on failure.
  const toggleEmailAllowed = async (next) => {
    if (!companyId || !hotelId) return;
    setEmailSaving(true);
    setEmailAllowed(next);
    try {
      const token = sessionStorage.getItem('token');
      const res = await fetch(
        `${config.API_BASE_URL}/reports/night-audit/email-setting`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ companyId, hotelId, enabled: next }),
        },
      );
      if (!res.ok) throw new Error(await res.text());
      notify(next ? 'Night audit email enabled' : 'Night audit email disabled');
    } catch (e) {
      setEmailAllowed(!next);
      notify('Failed to update email setting', 'error');
      console.error(e);
    } finally {
      setEmailSaving(false);
    }
  };

  useEffect(() => { load(); loadEmailSetting(); /* eslint-disable-next-line */ }, []);

  const exportExcel = () => {
    if (!data) return;
    const wb = XLSX.utils.book_new();

    const summary = [
      ['Hotel', data.hotelName || ''],
      ['Date', new Date(data.auditDate).toLocaleDateString('en-IN')],
      [],
      ['Revenue Breakdown', ''],
      ['Room Revenue',     Number(data.revenue.roomRevenue || 0)],
      ['Restaurant',       Number(data.revenue.restaurantRevenue || 0)],
      ['Spa',              Number(data.revenue.spaRevenue || 0)],
      ['Laundry',          Number(data.revenue.laundryRevenue || 0)],
      ['Mini-Bar',         Number(data.revenue.miniBarRevenue || 0)],
      ['Extra Services',   Number(data.revenue.extraServicesRevenue || 0)],
      ['Total Revenue',    Number(data.revenue.total || 0)],
      [],
      ['Collections', ''],
      ['Cash',   Number(data.collections.cashCollection || 0)],
      ['Card',   Number(data.collections.cardCollection || 0)],
      ['UPI',    Number(data.collections.upiCollection || 0)],
      ['Wallet', Number(data.collections.walletCollection || 0)],
      ['Total',  Number(data.collections.totalCollection || 0)],
      [],
      ['Occupancy', ''],
      ['Total Rooms',    data.occupancy.totalRooms],
      ['Occupied',       data.occupancy.occupiedRooms],
      ['Occupancy %',    Number(data.occupancy.occupancyPct || 0).toFixed(2)],
      ['ARR',            Number(data.occupancy.arr || 0).toFixed(2)],
      ['RevPAR',         Number(data.occupancy.revPAR || 0).toFixed(2)],
      ['Check-ins',      data.occupancy.totalCheckins],
      ['Check-outs',     data.occupancy.totalCheckouts],
      ['Cancellations',  data.occupancy.cancelledBookings],
      ['No-shows',       data.occupancy.noShowBookings],
      ['In-house Guests', data.occupancy.inHouseGuests],
      [],
      ['GST Breakdown', ''],
      ['CGST', Number(data.gst.cgst || 0)],
      ['SGST', Number(data.gst.sgst || 0)],
      ['IGST', Number(data.gst.igst || 0)],
      ['Total GST', Number(data.gst.totalGst || 0)],
    ];
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(summary), 'Summary');

    const cashierSheet = [['Cashier', 'Cash', 'Total', 'Txn #']];
    (data.cashiers || []).forEach((c) =>
      cashierSheet.push([
        c.cashierName, Number(c.cashCollected || 0),
        Number(c.totalCollected || 0), c.txnCount,
      ]),
    );
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(cashierSheet), 'Cashiers');

    XLSX.writeFile(wb, `NightAudit_${data.hotelName || 'Hotel'}_${date}.xlsx`);
  };

  const sumCashier = useMemo(() => {
    if (!data?.cashiers) return { cash: 0, total: 0, txn: 0 };
    return data.cashiers.reduce(
      (acc, c) => ({
        cash:  acc.cash  + Number(c.cashCollected || 0),
        total: acc.total + Number(c.totalCollected || 0),
        txn:   acc.txn   + Number(c.txnCount || 0),
      }),
      { cash: 0, total: 0, txn: 0 },
    );
  }, [data]);

  return (
    <Container maxWidth='xl' sx={{ py: 3 }}>
      <Box className='no-print' display='flex' justifyContent='space-between' alignItems='center' mb={2} flexWrap='wrap' gap={1}>
        <Typography variant='h5' fontWeight='700'>Night Audit Report</Typography>
        <Box display='flex' gap={1} alignItems='center' flexWrap='wrap'>
          <FormControlLabel
            sx={{ mr: 1 }}
            control={
              <Switch
                checked={emailAllowed}
                disabled={emailSaving || !companyId || !hotelId}
                onChange={(e) => toggleEmailAllowed(e.target.checked)}
              />
            }
            label='Email this report'
          />
          <TextField type='date' size='small' value={date}
            onChange={(e) => setDate(e.target.value)}
            InputLabelProps={{ shrink: true }} />
          <Button variant='contained' startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
            Load
          </Button>
          <Button variant='outlined' startIcon={<PrintIcon />} onClick={() => window.print()} disabled={!data}>
            Print
          </Button>
          <Button variant='outlined' startIcon={<DownloadIcon />} onClick={exportExcel} disabled={!data}>
            Excel
          </Button>
        </Box>
      </Box>

      {loading && (
        <Box display='flex' justifyContent='center' py={6}><CircularProgress /></Box>
      )}

      {!loading && data && (
        <Box id='night-audit-print' sx={{ background: '#fff', p: { xs: 1, md: 2 } }}>
          <Box mb={2}>
            <Typography variant='h6' fontWeight='700'>{data.hotelName}</Typography>
            <Typography variant='body2' color='text.secondary'>
              Audit Date: <strong>{new Date(data.auditDate).toLocaleDateString('en-IN', {
                day: '2-digit', month: 'short', year: 'numeric',
              })}</strong>
            </Typography>
          </Box>

          {/* ============ TOP KPI CARDS ============ */}
          <Grid container spacing={2}>
            <Grid item xs={6} sm={4} md={2}>
              <KpiCard title='Total Revenue' value={`${currency} ${fmtMoney(data.revenue.total)}`} color='#1976d2' />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <KpiCard title='Collections' value={`${currency} ${fmtMoney(data.collections.totalCollection)}`} color='#2e7d32' />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <KpiCard title='Occupancy' value={Number(data.occupancy.occupancyPct || 0).toFixed(1)} suffix='%' color='#ed6c02' />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <KpiCard title='ARR' value={`${currency} ${fmtMoney(data.occupancy.arr)}`} color='#9c27b0' />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <KpiCard title='RevPAR' value={`${currency} ${fmtMoney(data.occupancy.revPAR)}`} color='#0288d1' />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <KpiCard
                title='Red Flags'
                value={data.flags.totalRedFlags}
                color={data.flags.totalRedFlags > 0 ? '#c62828' : '#2e7d32'}
                sub={data.flags.totalRedFlags > 0 ? 'See verifications' : 'All clear'} />
            </Grid>
          </Grid>

          {/* ============ REVENUE + COLLECTIONS ============ */}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <SectionTitle>Revenue Breakdown</SectionTitle>
              <TableContainer component={Card}>
                <Table size='small'>
                  <TableBody>
                    <TableRow><TableCell>Room</TableCell><TableCell align='right'>{currency} {fmtMoney(data.revenue.roomRevenue)}</TableCell></TableRow>
                    <TableRow><TableCell>Restaurant</TableCell><TableCell align='right'>{currency} {fmtMoney(data.revenue.restaurantRevenue)}</TableCell></TableRow>
                    <TableRow><TableCell>Spa</TableCell><TableCell align='right'>{currency} {fmtMoney(data.revenue.spaRevenue)}</TableCell></TableRow>
                    <TableRow><TableCell>Laundry</TableCell><TableCell align='right'>{currency} {fmtMoney(data.revenue.laundryRevenue)}</TableCell></TableRow>
                    <TableRow><TableCell>Mini-Bar</TableCell><TableCell align='right'>{currency} {fmtMoney(data.revenue.miniBarRevenue)}</TableCell></TableRow>
                    <TableRow><TableCell>Extra Services</TableCell><TableCell align='right'>{currency} {fmtMoney(data.revenue.extraServicesRevenue)}</TableCell></TableRow>
                    <TableRow sx={{ bgcolor: '#eef5ff' }}>
                      <TableCell><strong>Total Revenue</strong></TableCell>
                      <TableCell align='right'><strong>{currency} {fmtMoney(data.revenue.total)}</strong></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>

            <Grid item xs={12} md={6}>
              <SectionTitle>Collections (Today)</SectionTitle>
              <TableContainer component={Card}>
                <Table size='small'>
                  <TableBody>
                    <TableRow><TableCell>Cash</TableCell><TableCell align='right'>{currency} {fmtMoney(data.collections.cashCollection)}</TableCell></TableRow>
                    <TableRow><TableCell>Card</TableCell><TableCell align='right'>{currency} {fmtMoney(data.collections.cardCollection)}</TableCell></TableRow>
                    <TableRow><TableCell>UPI</TableCell><TableCell align='right'>{currency} {fmtMoney(data.collections.upiCollection)}</TableCell></TableRow>
                    <TableRow><TableCell>Wallet</TableCell><TableCell align='right'>{currency} {fmtMoney(data.collections.walletCollection)}</TableCell></TableRow>
                    <TableRow sx={{ bgcolor: '#eafaf1' }}>
                      <TableCell><strong>Total Collected</strong></TableCell>
                      <TableCell align='right'><strong>{currency} {fmtMoney(data.collections.totalCollection)}</strong></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>

          {/* ============ OCCUPANCY + ROOM STATUS ============ */}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <SectionTitle>Booking Activity</SectionTitle>
              <TableContainer component={Card}>
                <Table size='small'>
                  <TableBody>
                    <TableRow><TableCell>Total Check-ins</TableCell><TableCell align='right'>{data.occupancy.totalCheckins}</TableCell></TableRow>
                    <TableRow><TableCell>Total Check-outs</TableCell><TableCell align='right'>{data.occupancy.totalCheckouts}</TableCell></TableRow>
                    <TableRow><TableCell>In-house Guests</TableCell><TableCell align='right'>{data.occupancy.inHouseGuests}</TableCell></TableRow>
                    <TableRow><TableCell>Cancellations</TableCell><TableCell align='right'>{data.occupancy.cancelledBookings}</TableCell></TableRow>
                    <TableRow><TableCell>No-shows</TableCell><TableCell align='right'>{data.occupancy.noShowBookings}</TableCell></TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>

            <Grid item xs={12} md={6}>
              <SectionTitle>Room Status</SectionTitle>
              <TableContainer component={Card}>
                <Table size='small'>
                  <TableBody>
                    <TableRow><TableCell>Total Rooms</TableCell><TableCell align='right'>{data.rooms.totalRooms}</TableCell></TableRow>
                    <TableRow><TableCell>Occupied</TableCell><TableCell align='right'>{data.rooms.occupied}</TableCell></TableRow>
                    <TableRow><TableCell>Vacant</TableCell><TableCell align='right'>{data.rooms.vacant}</TableCell></TableRow>
                    <TableRow><TableCell>Dirty</TableCell><TableCell align='right'>{data.rooms.dirty}</TableCell></TableRow>
                    <TableRow><TableCell>Clean</TableCell><TableCell align='right'>{data.rooms.clean}</TableCell></TableRow>
                    <TableRow><TableCell>Out Of Order</TableCell><TableCell align='right'>{data.rooms.outOfOrder}</TableCell></TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>

          {/* ============ BOOKED ROOMS (which rooms sold + amount) ============ */}
          <SectionTitle>Booked Rooms</SectionTitle>
          <TableContainer component={Card}>
            <Table size='small'>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell>Room</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell align='right'>Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(data.bookedRooms || []).length === 0 && (
                  <TableRow><TableCell colSpan={3} align='center'>No rooms booked for this night.</TableCell></TableRow>
                )}
                {(data.bookedRooms || []).map((r, i) => (
                  <TableRow key={i}>
                    <TableCell>{r.roomName ? `${r.roomName} (${r.roomNumber})` : r.roomNumber}</TableCell>
                    <TableCell>{r.roomType || '-'}</TableCell>
                    <TableCell align='right'>{currency} {fmtMoney(r.amount)}</TableCell>
                  </TableRow>
                ))}
                {(data.bookedRooms || []).length > 0 && (
                  <TableRow sx={{ bgcolor: '#fafafa' }}>
                    <TableCell colSpan={2} sx={{ fontWeight: 700 }}>
                      Total ({data.bookedRooms.length} room{data.bookedRooms.length > 1 ? 's' : ''})
                    </TableCell>
                    <TableCell align='right' sx={{ fontWeight: 700 }}>
                      {currency} {fmtMoney(data.bookedRooms.reduce((s, r) => s + Number(r.amount || 0), 0))}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* ============ CASHIER-WISE ============ */}
          <SectionTitle>Cashier-wise Collections</SectionTitle>
          <TableContainer component={Card}>
            <Table size='small'>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell>Cashier</TableCell>
                  <TableCell align='right'>Cash</TableCell>
                  <TableCell align='right'>Total</TableCell>
                  <TableCell align='right'>Txn #</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(data.cashiers || []).length === 0 && (
                  <TableRow><TableCell colSpan={4} align='center'>No cashier activity today.</TableCell></TableRow>
                )}
                {(data.cashiers || []).map((c) => (
                  <TableRow key={c.userId}>
                    <TableCell>{c.cashierName}</TableCell>
                    <TableCell align='right'>{currency} {fmtMoney(c.cashCollected)}</TableCell>
                    <TableCell align='right'>{currency} {fmtMoney(c.totalCollected)}</TableCell>
                    <TableCell align='right'>{c.txnCount}</TableCell>
                  </TableRow>
                ))}
                {(data.cashiers || []).length > 0 && (
                  <TableRow sx={{ bgcolor: '#eef5ff' }}>
                    <TableCell><strong>Total</strong></TableCell>
                    <TableCell align='right'><strong>{currency} {fmtMoney(sumCashier.cash)}</strong></TableCell>
                    <TableCell align='right'><strong>{currency} {fmtMoney(sumCashier.total)}</strong></TableCell>
                    <TableCell align='right'><strong>{sumCashier.txn}</strong></TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* ============ AUDIT FLAGS ============ */}
          <SectionTitle>
            <Box display='flex' alignItems='center' gap={1}>
              Audit Verification
              {data.flags.totalRedFlags > 0 && (
                <Chip size='small' icon={<WarningAmberIcon />} color='error' label={`${data.flags.totalRedFlags} issue(s)`} />
              )}
            </Box>
          </SectionTitle>
          <Grid container spacing={1}>
            {[
              ['Unposted Spa charges',     data.flags.unpostedSpaCharges],
              ['Unposted Laundry charges', data.flags.unpostedLaundryCharges],
              ['Pending Laundry orders',   data.flags.pendingLaundryOrders],
              ['Missing checkouts',        data.flags.missingCheckouts],
              ['Unclosed restaurant orders', data.flags.unclosedRestaurantOrders],
              ['Negative stock items',     data.flags.negativeStockItems],
            ].map(([label, n]) => (
              <Grid item xs={6} sm={4} md={2} key={label}>
                <Card sx={{
                  p: 1.5,
                  textAlign: 'center',
                  borderTop: `3px solid ${n > 0 ? '#c62828' : '#2e7d32'}`,
                }}>
                  <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>
                    {label}
                  </Typography>
                  <Typography variant='h5' fontWeight='700' color={n > 0 ? 'error' : 'success.main'}>
                    {n}
                  </Typography>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* ============ GST ============ */}
          <SectionTitle>GST Breakdown</SectionTitle>
          <TableContainer component={Card} sx={{ mb: 4 }}>
            <Table size='small'>
              <TableBody>
                <TableRow><TableCell>CGST</TableCell><TableCell align='right'>{currency} {fmtMoney(data.gst.cgst)}</TableCell></TableRow>
                <TableRow><TableCell>SGST</TableCell><TableCell align='right'>{currency} {fmtMoney(data.gst.sgst)}</TableCell></TableRow>
                <TableRow><TableCell>IGST</TableCell><TableCell align='right'>{currency} {fmtMoney(data.gst.igst)}</TableCell></TableRow>
                <TableRow sx={{ bgcolor: '#eef5ff' }}>
                  <TableCell><strong>Total GST</strong></TableCell>
                  <TableCell align='right'><strong>{currency} {fmtMoney(data.gst.totalGst)}</strong></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>

          <Divider sx={{ my: 3 }} />
          <Typography variant='caption' color='text.secondary'>
            Report generated at {new Date().toLocaleString('en-IN')}. Numbers reflect transactions
            recorded up to this moment for the selected date.
          </Typography>
        </Box>
      )}

      <Snackbar open={snack.open} autoHideDuration={3000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert severity={snack.sev}>{snack.msg}</Alert>
      </Snackbar>
    </Container>
  );
}
